/**
 * Returns true when `host` is exactly `domain` or a subdomain of it
 * (e.g. "editor.rive.app" for domain "rive.app").
 *
 * Unlike a bare `endsWith` check this rejects lookalikes such as
 * "notrive.app".
 */
export function isTrustedHost(host: string, domain: string): boolean {
  return host === domain || host.endsWith(`.${domain}`);
}

/**
 * Sanitizes a redirect URL from query parameters to prevent open redirects.
 *
 * Allows:
 * - Absolute http(s) URLs on the exact configured domain or a subdomain of it
 * - Path-only strings (e.g. `/account/settings`)
 *
 * Returns an empty string for anything else.
 */
export function sanitizeRedirect(
  redirect: string | string[] | undefined
): string {
  if (typeof redirect !== 'string') {
    return '';
  }

  try {
    const url = new URL(redirect);
    // The result is passed to `location.assign`, so a trusted host is not
    // enough: `javascript://rive.app/%0a...` has host "rive.app" but runs as
    // script.
    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      return '';
    }
    const domain = process.env.NEXT_PUBLIC_SITE_DOMAIN ?? 'rive.app';
    if (!isTrustedHost(url.host, domain)) {
      return '';
    }
    return redirect;
  } catch {
    // URL constructor throws for values without a hostname.
    // Accept bare pathnames like /foo/bar or /foo/bar/. Browsers treat `\` as
    // `/` and drop tabs and newlines, so `/\evil.com` and `/\t/evil.com` would
    // resolve to evil.com.
    const pathnameCheck = redirect.match(/^(\/[^/\\\s]+)+\/?$/i);
    if (!pathnameCheck) {
      return '';
    }
    return redirect;
  }
}

/**
 * Builds a cross-flow link that preserves the current redirect param.
 * e.g. buildAuthLink('/signup', '/some/page') => '/signup?redirect=%2Fsome%2Fpage'
 */
export function buildAuthLink(basePath: string, redirect: string): string {
  if (redirect) {
    return `${basePath}?redirect=${encodeURIComponent(redirect)}`;
  }
  return basePath;
}
